import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Presentation, ChevronLeft, ChevronRight, 
  Printer, ArrowRight, Brain, Target, Shield,
  TrendingUp, Users, AlertTriangle, Lightbulb, Sparkles,
  BarChart3, Eye, Zap, CheckCircle2, Quote, 
  MessageSquare, ShoppingBag, DollarSign, Clock,
  XCircle, Package, Percent, LineChart, Heart,
  Store, CreditCard, Globe, Truck, MousePointer,
  Calculator, Gauge, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, prefix = "", suffix = "", className = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{count}{suffix}
    </span>
  );
};

// Interactive Reveal Component
const RevealList = ({ items, className = "" }) => {
  const [revealed, setRevealed] = useState([]);

  const revealNext = () => {
    if (revealed.length < items.length) {
      setRevealed([...revealed, revealed.length]);
    }
  };

  const revealAll = () => {
    setRevealed(items.map((_, i) => i));
  };

  return (
    <div className={className}>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div 
            key={idx} 
            className={`transform transition-all duration-500 ${
              revealed.includes(idx) 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 -translate-x-8'
            }`}
          >
            {item}
          </div>
        ))}
      </div>
      {revealed.length < items.length && (
        <div className="mt-4 flex gap-2">
          <Button 
            onClick={revealNext} 
            size="sm" 
            className="bg-[#95BF47]/20 hover:bg-[#95BF47]/30 text-[#95BF47]"
          >
            Reveal Next
          </Button>
          <Button 
            onClick={revealAll} 
            size="sm" 
            variant="ghost"
            className="text-[#64748B] hover:text-[#94A3B8]"
          >
            Show All
          </Button>
        </div>
      )}
    </div>
  );
};

const ShopifyTEDTalkPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Shopify green theme
  const sectionColors = {
    opening: "from-[#0A0F1C] via-[#111827] to-[#0A0F1C]",
    problem: "from-[#1a0f0a] via-[#111827] to-[#0A0F1C]",
    insight: "from-[#0a1a1a] via-[#111827] to-[#0A0F1C]",
    reframe: "from-[#0f1a0a] via-[#111827] to-[#0A0F1C]",
    solution: "from-[#0a1a0f] via-[#111827] to-[#0A0F1C]",
    impact: "from-[#0a0f1a] via-[#111827] to-[#0A0F1C]",
    closing: "from-[#0A0F1C] via-[#1a1a2a] to-[#0A0F1C]"
  };

  const slides = [
    {
      id: 0,
      type: "title",
      section: "opening",
      title: "Why Most Shopify Stores Are Pricing Blind",
      subtitle: "— and How to Stop Guessing",
      badge: "For Shopify Merchants",
      note: "A practical guide for founders, operators, and growth leads",
      speakerNotes: "This talk is specifically for Shopify merchants. Make it personal - you're speaking to people who've lived this reality. No jargon, no theory - just the truth about pricing.",
      visual: "🛍️"
    },
    {
      id: 1,
      type: "story-sequence",
      section: "opening",
      title: "Sound Familiar?",
      sequence: [
        { text: "You launch a product", icon: "🚀" },
        { text: "You pick a price that feels right", icon: "🤔" },
        { text: "You watch conversion", icon: "👀" },
        { text: "You tweak it", icon: "🔧" },
        { text: "You discount", icon: "🏷️" },
        { text: "You raise it", icon: "📈" },
        { text: "You hope", icon: "🤞" }
      ],
      followup: "And if you're successful, you do this again… for ten products… then fifty… then hundreds.",
      speakerNotes: "Walk through these slowly. Let merchants see themselves. This is their daily reality. The humor is in the recognition - they'll be nodding."
    },
    {
      id: 2,
      type: "stress-point",
      section: "opening",
      title: "When Pricing Becomes Stress",
      subtitle: "Every price change feels risky:",
      fears: [
        { text: "Will conversion drop?", icon: ArrowDownRight, color: "text-red-400" },
        { text: "Will margins collapse?", icon: DollarSign, color: "text-yellow-400" },
        { text: "Will customers churn?", icon: Users, color: "text-orange-400" },
        { text: "Will Shopify Ads suddenly stop working?", icon: MousePointer, color: "text-purple-400" }
      ],
      truth: "Most Shopify merchants don't actually know. They're guessing — just very professionally.",
      speakerNotes: "Pause after each fear. These are real anxieties. The punchline 'guessing professionally' should get knowing laughs."
    },
    {
      id: 3,
      type: "shopify-praise",
      section: "problem",
      title: "Shopify Has Given Entrepreneurs Incredible Tools",
      tools: [
        { name: "Storefronts in minutes", icon: Store },
        { name: "Payments out of the box", icon: CreditCard },
        { name: "Logistics at scale", icon: Truck },
        { name: "Global reach", icon: Globe }
      ],
      but: "But pricing?",
      speakerNotes: "Acknowledge Shopify's strengths first. This builds credibility with your audience. Then the pivot to 'But pricing?' creates the gap."
    },
    {
      id: 4,
      type: "current-state",
      section: "problem",
      title: "Pricing Is Still Mostly...",
      methods: [
        { text: "Gut instinct", emoji: "🎲" },
        { text: "Competitor screenshots", emoji: "📸" },
        { text: "A/B tests that take weeks", emoji: "⏳" },
        { text: "Post-mortems after revenue drops", emoji: "📉" }
      ],
      speakerNotes: "Each of these is a mini-story. 'Competitor screenshots' - we've all done it at 2am. 'Post-mortems after revenue drops' - the painful autopsy."
    },
    {
      id: 5,
      type: "uncomfortable-truth",
      section: "problem",
      title: "The Uncomfortable Truth",
      main: "Most Shopify stores don't lose money because demand is weak.",
      emphasis: "They lose money because prices are wrong — and they don't know how wrong.",
      speakerNotes: "This is your thesis statement for the problem. Say it slowly. Let it land. This reframes the entire conversation."
    },
    {
      id: 6,
      type: "cascade",
      section: "problem",
      title: "When Prices Are Wrong, Everything Else Breaks",
      effects: [
        { text: "Ads look inefficient", icon: MousePointer },
        { text: "CAC looks too high", icon: DollarSign },
        { text: "LTV looks fragile", icon: TrendingUp },
        { text: "Discounts become addictive", icon: Percent }
      ],
      conclusion: "Pricing quietly becomes the hidden tax on growth.",
      speakerNotes: "Show the cascade effect. Bad pricing doesn't just hurt margins - it corrupts your entire understanding of unit economics."
    },
    {
      id: 7,
      type: "advice-challenge",
      section: "insight",
      title: "You've Probably Heard This Advice:",
      advice: "\"Just test prices.\"",
      speakerNotes: "Say this like you're quoting a guru or a Twitter thread. Then pause. Your next slides will dismantle this advice."
    },
    {
      id: 8,
      type: "three-problems",
      section: "insight",
      title: "But Testing Prices Has Three Big Problems",
      problems: [
        {
          number: "1",
          title: "It's slow",
          description: "You wait days or weeks for clean data.",
          icon: Clock,
          color: "yellow"
        },
        {
          number: "2",
          title: "It's risky",
          description: "You test on real customers, real traffic, real revenue.",
          icon: AlertTriangle,
          color: "red"
        },
        {
          number: "3",
          title: "It lies to you",
          description: "Because seasonality, ads, promotions, and randomness all get mixed in.",
          icon: XCircle,
          color: "purple"
        }
      ],
      speakerNotes: "Three clear problems. Each one resonates with merchants who've tried testing. The third is often the surprise - they didn't realize their 'data' was noise."
    },
    {
      id: 9,
      type: "consequence",
      section: "insight",
      title: "So What Happens?",
      outcomes: [
        "Merchants stop experimenting",
        "Or they discount aggressively",
        "Or they freeze prices entirely"
      ],
      punchline: "Which means the most important lever in your business becomes the least understood.",
      speakerNotes: "Show the paralysis. None of these outcomes are good. The punchline reframes pricing as THE lever - and it's being ignored."
    },
    {
      id: 10,
      type: "reframe",
      section: "reframe",
      title: "The Mental Shift That Changes Everything",
      old: {
        label: "WHAT YOU'RE TOLD",
        text: "Find the right price"
      },
      new: {
        label: "WHAT YOU ACTUALLY NEED",
        text: "Understand the consequences of price decisions"
      },
      speakerNotes: "This is your pivot moment. The reframe from 'finding' to 'understanding'. Say it slowly. This changes the entire conversation."
    },
    {
      id: 11,
      type: "what-you-want",
      section: "reframe",
      title: "What You Actually Want to Know",
      questions: [
        { text: "How sensitive are my customers?", icon: Users },
        { text: "Where does profit really peak?", icon: TrendingUp },
        { text: "How much volume can I lose safely?", icon: Package },
        { text: "What's the risk of pushing too far?", icon: AlertTriangle },
        { text: "What happens if costs change?", icon: DollarSign }
      ],
      conclusion: "You don't need a price. You need clarity.",
      speakerNotes: "Reveal these one by one. Each question is a real concern merchants have. The conclusion reframes the entire value proposition."
    },
    {
      id: 12,
      type: "ai-intro",
      section: "reframe",
      title: "That's Where AI — Used Properly — Becomes Powerful",
      visual: "🧠✨",
      speakerNotes: "Brief bridge slide. AI is the enabler, but not the hero. The hero is clarity. AI just makes it possible."
    },
    {
      id: 13,
      type: "pricestrike-intro",
      section: "solution",
      title: "PriceStrike Was Built Around One Simple Idea",
      question: "What if Shopify merchants could see the impact of a price change before touching their live store?",
      howNot: [
        { text: "Not by guessing", icon: "❌🎲" },
        { text: "Not by waiting weeks", icon: "❌⏳" },
        { text: "But by simulating outcomes", icon: "✅🔮" }
      ],
      speakerNotes: "This is your product introduction - but it's framed as an idea, not a pitch. The 'What if' makes it aspirational."
    },
    {
      id: 14,
      type: "what-it-shows",
      section: "solution",
      title: "PriceStrike Doesn't Just Say \"Here's the Price\"",
      subtitle: "It shows:",
      features: [
        { name: "The demand curve", icon: LineChart, description: "How volume responds to price" },
        { name: "The margin curve", icon: BarChart3, description: "Where profit actually lives" },
        { name: "The revenue sweet spot", icon: Target, description: "The zone where growth and margin meet" },
        { name: "The danger zones", icon: AlertTriangle, description: "Where you're taking real risk" }
      ],
      speakerNotes: "Show what visibility looks like. Each feature answers one of the earlier questions. Make it concrete."
    },
    {
      id: 15,
      type: "question-flip",
      section: "solution",
      title: "A Different Question",
      before: {
        question: "Should I raise prices?",
        emotion: "Fear, uncertainty"
      },
      after: {
        question: "What happens if I do?",
        emotion: "Confidence, clarity"
      },
      conclusion: "That's a completely different relationship with pricing.",
      speakerNotes: "The question flip is the magic. From binary (yes/no) to exploratory (what if). This is empowerment."
    },
    {
      id: 16,
      type: "impact-list",
      section: "impact",
      title: "When Merchants Price With Visibility Instead of Fear",
      outcomes: [
        { text: "Discounts become strategic, not reactive", icon: Percent },
        { text: "Ads perform better because prices make sense", icon: MousePointer },
        { text: "Margin stops being a surprise", icon: DollarSign },
        { text: "Growth becomes calmer — not chaotic", icon: TrendingUp }
      ],
      speakerNotes: "Show the transformation. Each outcome is a pain point flipped. Merchants will recognize the 'after' state they want."
    },
    {
      id: 17,
      type: "emotional-peak",
      section: "impact",
      title: "Most Importantly",
      message: "Founders stop arguing with their gut",
      emphasis: "and start trusting their decisions.",
      speakerNotes: "This is emotional. The internal argument every founder has. 'Should I trust my gut or the data?' Now you don't have to choose."
    },
    {
      id: 18,
      type: "values",
      section: "impact",
      title: "This Isn't About Extracting Every Last Penny",
      subtitle: "It's about building businesses that are:",
      values: [
        { text: "Sustainable", icon: Heart, color: "text-green-400" },
        { text: "Confident", icon: Shield, color: "text-blue-400" },
        { text: "Resilient under pressure", icon: Zap, color: "text-purple-400" }
      ],
      speakerNotes: "Counter the 'price gouging' objection. This is about healthy businesses, not maximizing extraction. Values-led positioning."
    },
    {
      id: 19,
      type: "bigger-picture",
      section: "closing",
      title: "The Bigger Idea",
      statements: [
        "Shopify changed who can build a business.",
        "The next change is about how confidently those businesses are run."
      ],
      speakerNotes: "Zoom out. Connect to the Shopify mission. Democratizing commerce is step one. Democratizing business intelligence is step two."
    },
    {
      id: 20,
      type: "reframing-close",
      section: "closing",
      title: "Pricing Doesn't Have to Be...",
      oldWays: [
        { text: "Guesswork", cross: true },
        { text: "Scary", cross: true },
        { text: "Reactive", cross: true }
      ],
      speakerNotes: "Three quick rejections. Each one should feel like a weight being lifted."
    },
    {
      id: 21,
      type: "vision",
      section: "closing",
      title: "The Future of Commerce",
      vision: "The future of commerce isn't just faster checkouts or better ads.",
      emphasis: "It's merchants who finally understand the economics of their own decisions.",
      speakerNotes: "Paint the vision. Commerce isn't just about transactions - it's about understanding. This is the next frontier."
    },
    {
      id: 22,
      type: "final",
      section: "closing",
      message: "And when pricing becomes clear —",
      emphasis: "everything else gets easier.",
      closing: "Thank you.",
      speakerNotes: "Simple, clean close. The emphasis line is your gift. 'Thank you' should be warm and genuine. Hold for applause."
    }
  ];

  const getSectionColor = (section) => sectionColors[section] || sectionColors.opening;

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(currentSlide + 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(currentSlide - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      e.preventDefault();
      nextSlide();
    } else if (e.key === 'ArrowLeft') {
      prevSlide();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);

  const handlePrint = () => {
    window.print();
  };

  const renderSlide = (slide) => {
    const bgGradient = getSectionColor(slide.section);
    
    switch (slide.type) {
      case "title":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <div className="mb-4">
              <span className="bg-[#95BF47]/20 text-[#95BF47] px-4 py-1 rounded-full text-sm font-medium">
                {slide.badge}
              </span>
            </div>
            <div className="text-6xl mb-6">{slide.visual}</div>
            <h1 className="text-3xl md:text-5xl font-bold text-[#F8FAFC] mb-4 leading-tight">
              {slide.title}
            </h1>
            <p className="text-xl md:text-3xl text-[#95BF47] font-medium mb-8">
              {slide.subtitle}
            </p>
            <p className="text-sm text-[#64748B] max-w-lg">{slide.note}</p>
          </div>
        );

      case "story-sequence":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <div className="flex flex-wrap gap-3 mb-6">
              {slide.sequence.map((item, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#111827]/80 border border-[#95BF47]/20 rounded-lg px-4 py-2 flex items-center gap-2 hover:border-[#95BF47]/50 transition-all"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span className="text-[#94A3B8] text-sm">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="text-lg text-[#94A3B8] italic">{slide.followup}</p>
          </div>
        );

      case "stress-point":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-2">{slide.title}</h2>
            <p className="text-lg text-[#94A3B8] mb-6">{slide.subtitle}</p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {slide.fears.map((fear, idx) => {
                const Icon = fear.icon;
                return (
                  <div key={idx} className="bg-[#111827]/80 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
                    <Icon className={`w-6 h-6 ${fear.color}`} />
                    <span className="text-[#F8FAFC]">{fear.text}</span>
                  </div>
                );
              })}
            </div>
            <div className="bg-[#95BF47]/10 border-l-4 border-[#95BF47] p-4 rounded-r-xl">
              <p className="text-lg text-[#F8FAFC] font-medium">{slide.truth}</p>
            </div>
          </div>
        );

      case "shopify-praise":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-8">{slide.title}</h2>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {slide.tools.map((tool, idx) => {
                const Icon = tool.icon;
                return (
                  <div key={idx} className="bg-[#95BF47]/10 border border-[#95BF47]/30 rounded-xl p-5 flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#95BF47]/20 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#95BF47]" />
                    </div>
                    <span className="text-[#F8FAFC] font-medium">{tool.name}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-3xl md:text-4xl font-bold text-[#94A3B8] text-center">{slide.but}</p>
          </div>
        );

      case "current-state":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-red-400 font-semibold mb-4 uppercase tracking-wider">The Problem</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-8">{slide.title}</h2>
            <div className="grid grid-cols-2 gap-4">
              {slide.methods.map((method, idx) => (
                <div key={idx} className="bg-[#111827]/80 border border-[#64748B]/30 rounded-xl p-5 flex items-center gap-4 hover:border-[#64748B]/60 transition-all">
                  <span className="text-4xl">{method.emoji}</span>
                  <span className="text-[#94A3B8] text-lg">{method.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "uncomfortable-truth":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-red-400 font-semibold mb-8 uppercase tracking-wider">The Uncomfortable Truth</p>
            <p className="text-xl md:text-2xl text-[#94A3B8] mb-6 max-w-3xl">{slide.main}</p>
            <p className="text-2xl md:text-3xl font-bold text-red-400 max-w-3xl">{slide.emphasis}</p>
          </div>
        );

      case "cascade":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-red-400 font-semibold mb-4 uppercase tracking-wider">The Cascade Effect</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {slide.effects.map((effect, idx) => {
                const Icon = effect.icon;
                return (
                  <React.Fragment key={idx}>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center gap-2">
                      <Icon className="w-5 h-5 text-red-400" />
                      <span className="text-[#F8FAFC] text-sm">{effect.text}</span>
                    </div>
                    {idx < slide.effects.length - 1 && (
                      <ArrowRight className="w-4 h-4 text-[#64748B]" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="bg-[#111827]/80 border-l-4 border-red-500 p-4 rounded-r-xl">
              <p className="text-lg text-red-400 font-medium">{slide.conclusion}</p>
            </div>
          </div>
        );

      case "advice-challenge":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-cyan-400 font-semibold mb-8 uppercase tracking-wider">The Insight</p>
            <p className="text-xl text-[#94A3B8] mb-6">{slide.title}</p>
            <div className="bg-[#111827]/80 border border-cyan-500/30 rounded-2xl p-8">
              <p className="text-3xl md:text-5xl font-bold text-[#F8FAFC]">{slide.advice}</p>
            </div>
          </div>
        );

      case "three-problems":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-12 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-cyan-400 font-semibold mb-3 uppercase tracking-wider">The Insight</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-4">{slide.title}</h2>
            <div className="space-y-3">
              {slide.problems.map((problem, idx) => {
                const Icon = problem.icon;
                const colors = {
                  yellow: "border-yellow-500/30 bg-yellow-500/10",
                  red: "border-red-500/30 bg-red-500/10",
                  purple: "border-purple-500/30 bg-purple-500/10"
                };
                const textColors = {
                  yellow: "text-yellow-400",
                  red: "text-red-400",
                  purple: "text-purple-400"
                };
                return (
                  <div key={idx} className={`${colors[problem.color]} border rounded-xl p-4`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 ${colors[problem.color]} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <span className={`font-bold ${textColors[problem.color]}`}>{problem.number}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`w-5 h-5 ${textColors[problem.color]}`} />
                          <p className="text-lg font-semibold text-[#F8FAFC]">{problem.title}</p>
                        </div>
                        <p className="text-[#94A3B8] text-sm">{problem.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "consequence":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-8">{slide.title}</h2>
            <div className="space-y-3 mb-8">
              {slide.outcomes.map((outcome, idx) => (
                <p key={idx} className="text-xl text-[#94A3B8]">
                  {idx === 0 ? "" : "Or "}{outcome}
                </p>
              ))}
            </div>
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6 max-w-2xl">
              <p className="text-lg text-cyan-400 font-medium">{slide.punchline}</p>
            </div>
          </div>
        );

      case "reframe":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-[#95BF47] font-semibold mb-6 uppercase tracking-wider">The Reframe</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-10">{slide.title}</h2>
            <div className="flex items-center gap-6 md:gap-12 flex-wrap justify-center">
              <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 md:p-8 text-center max-w-xs">
                <p className="text-xs text-red-400 mb-2 uppercase tracking-wider">{slide.old.label}</p>
                <p className="text-lg md:text-xl text-red-400 line-through">{slide.old.text}</p>
              </div>
              <div className="text-4xl">→</div>
              <div className="bg-[#95BF47]/10 border border-[#95BF47]/30 rounded-2xl p-6 md:p-8 text-center max-w-xs">
                <p className="text-xs text-[#95BF47] mb-2 uppercase tracking-wider">{slide.new.label}</p>
                <p className="text-lg md:text-xl text-[#95BF47] font-semibold">{slide.new.text}</p>
              </div>
            </div>
          </div>
        );

      case "what-you-want":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-[#95BF47] font-semibold mb-4 uppercase tracking-wider">The Reframe</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <RevealList 
              items={slide.questions.map((q, idx) => {
                const Icon = q.icon;
                return (
                  <div key={idx} className="flex items-center gap-4 bg-[#111827]/60 rounded-lg p-4">
                    <div className="w-10 h-10 bg-[#95BF47]/20 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#95BF47]" />
                    </div>
                    <p className="text-lg text-[#F8FAFC]">{q.text}</p>
                  </div>
                );
              })}
            />
            <div className="mt-6 bg-[#95BF47]/10 border-l-4 border-[#95BF47] p-4 rounded-r-xl">
              <p className="text-lg text-[#95BF47] font-semibold">{slide.conclusion}</p>
            </div>
          </div>
        );

      case "ai-intro":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <div className="text-6xl mb-8">{slide.visual}</div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC]">{slide.title}</h2>
          </div>
        );

      case "pricestrike-intro":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-[#95BF47] font-semibold mb-6 uppercase tracking-wider">The Solution</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <p className="text-xl md:text-2xl text-[#95BF47] mb-8 max-w-3xl font-medium">
              {slide.question}
            </p>
            <div className="flex gap-4 flex-wrap justify-center">
              {slide.howNot.map((item, idx) => (
                <div key={idx} className="bg-[#111827]/80 border border-[#95BF47]/30 rounded-xl px-5 py-3 flex items-center gap-2">
                  <span className="text-xl">{item.icon}</span>
                  <span className="text-[#94A3B8]">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "what-it-shows":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-12 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-[#95BF47] font-semibold mb-3 uppercase tracking-wider">The Solution</p>
            <h2 className="text-xl md:text-2xl font-bold text-[#F8FAFC] mb-1">{slide.title}</h2>
            <p className="text-lg text-[#94A3B8] mb-4">{slide.subtitle}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {slide.features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#95BF47]/30 rounded-xl p-4 hover:border-[#95BF47]/60 transition-all">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#95BF47]/10 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#95BF47]" />
                      </div>
                      <p className="text-[#F8FAFC] font-semibold">{feature.name}</p>
                    </div>
                    <p className="text-sm text-[#64748B] ml-13">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "question-flip":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-10">{slide.title}</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8 w-full max-w-3xl">
              <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
                <p className="text-xs text-red-400 mb-3 uppercase">Before</p>
                <p className="text-xl text-[#F8FAFC] font-medium mb-2">"{slide.before.question}"</p>
                <p className="text-sm text-red-400">{slide.before.emotion}</p>
              </div>
              <div className="bg-[#95BF47]/10 border border-[#95BF47]/30 rounded-2xl p-6 text-center">
                <p className="text-xs text-[#95BF47] mb-3 uppercase">After</p>
                <p className="text-xl text-[#F8FAFC] font-medium mb-2">"{slide.after.question}"</p>
                <p className="text-sm text-[#95BF47]">{slide.after.emotion}</p>
              </div>
            </div>
            <p className="text-lg text-[#94A3B8]">{slide.conclusion}</p>
          </div>
        );

      case "impact-list":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-blue-400 font-semibold mb-4 uppercase tracking-wider">The Impact</p>
            <h2 className="text-xl md:text-2xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <div className="space-y-3">
              {slide.outcomes.map((outcome, idx) => {
                const Icon = outcome.icon;
                return (
                  <div key={idx} className="bg-[#111827]/60 border border-blue-500/20 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <p className="text-lg text-[#F8FAFC]">{outcome.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "emotional-peak":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-blue-400 font-semibold mb-8 uppercase tracking-wider">The Impact</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">{slide.title}</h2>
            <p className="text-xl md:text-2xl text-[#94A3B8] mb-4">{slide.message}</p>
            <p className="text-2xl md:text-3xl font-bold text-blue-400">{slide.emphasis}</p>
          </div>
        );

      case "values":
        return (
          <div className={`flex flex-col justify-center h-full px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-2">{slide.title}</h2>
            <p className="text-lg text-[#94A3B8] mb-8">{slide.subtitle}</p>
            <div className="grid md:grid-cols-3 gap-4">
              {slide.values.map((value, idx) => {
                const Icon = value.icon;
                return (
                  <div key={idx} className="bg-[#111827]/80 border border-[#2563EB]/20 rounded-xl p-6 text-center hover:scale-105 transition-transform">
                    <Icon className={`w-12 h-12 ${value.color} mx-auto mb-3`} />
                    <p className="text-lg text-[#F8FAFC] font-medium">{value.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "bigger-picture":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-sm text-[#95BF47] font-semibold mb-8 uppercase tracking-wider">Closing</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-8">{slide.title}</h2>
            <div className="space-y-4 max-w-2xl">
              {slide.statements.map((statement, idx) => (
                <p key={idx} className={`text-xl md:text-2xl ${idx === 0 ? 'text-[#94A3B8]' : 'text-[#95BF47] font-medium'}`}>
                  {statement}
                </p>
              ))}
            </div>
          </div>
        );

      case "reframing-close":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-8">{slide.title}</h2>
            <div className="space-y-4">
              {slide.oldWays.map((way, idx) => (
                <p key={idx} className="text-2xl text-[#64748B] line-through">
                  {way.text}
                </p>
              ))}
            </div>
          </div>
        );

      case "vision":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 md:px-16 bg-gradient-to-br ${bgGradient}`}>
            <p className="text-xl md:text-2xl text-[#94A3B8] mb-6 max-w-3xl">{slide.vision}</p>
            <p className="text-2xl md:text-3xl font-bold text-[#F8FAFC] max-w-3xl">{slide.emphasis}</p>
          </div>
        );

      case "final":
        return (
          <div className={`flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br ${bgGradient}`}>
            <Sparkles className="w-16 h-16 text-[#95BF47] mb-8 animate-pulse" />
            <p className="text-xl md:text-2xl text-[#94A3B8] mb-4">{slide.message}</p>
            <p className="text-2xl md:text-4xl font-bold text-[#F8FAFC] mb-12">{slide.emphasis}</p>
            <p className="text-3xl text-[#95BF47] font-medium">{slide.closing}</p>
          </div>
        );

      default:
        return null;
    }
  };

  const currentSlideData = slides[currentSlide];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <style>
        {`
          @media print {
            body { background: white !important; }
            .no-print { display: none !important; }
          }
        `}
      </style>

      <section className="pt-32 pb-8 px-6 no-print">
        <div className="max-w-6xl mx-auto">
          {/* Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#95BF47]" />
                <span className="text-[#94A3B8]">Shopify Merchants Talk</span>
              </div>
              <span className="text-sm px-3 py-1 bg-[#95BF47]/10 text-[#95BF47] rounded-full capitalize">
                {currentSlideData.section}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#94A3B8] text-sm">
                {currentSlide + 1} / {slides.length}
              </span>
              <Button 
                onClick={() => setShowNotes(!showNotes)}
                variant="outline" 
                size="sm"
                className={`border-[#95BF47]/50 text-[#F8FAFC] hover:bg-[#95BF47]/10 ${showNotes ? 'bg-[#95BF47]/20' : ''}`}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Notes
              </Button>
              <Button 
                onClick={handlePrint}
                variant="outline" 
                size="sm"
                className="border-[#95BF47]/50 text-[#F8FAFC] hover:bg-[#95BF47]/10"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>

          {/* Slide Display */}
          <div className={`bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#95BF47]/30 rounded-2xl overflow-hidden shadow-2xl shadow-[#95BF47]/10 transition-opacity duration-300 ${isAnimating ? 'opacity-50' : 'opacity-100'}`}>
            <div className="aspect-video relative">
              {renderSlide(currentSlideData)}
            </div>
          </div>

          {/* Speaker Notes */}
          {showNotes && currentSlideData.speakerNotes && (
            <div className="mt-4 bg-[#111827] border border-yellow-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-semibold text-yellow-400">SPEAKER NOTES</span>
              </div>
              <p className="text-[#94A3B8] text-sm">{currentSlideData.speakerNotes}</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              variant="outline"
              className="border-[#95BF47]/50 text-[#F8FAFC] hover:bg-[#95BF47]/10 disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </Button>
            
            <div className="flex-1 mx-8">
              <div className="h-2 bg-[#111827] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#95BF47] to-[#7fa93d] transition-all duration-300"
                  style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2">
                {['Opening', 'Problem', 'Insight', 'Reframe', 'Solution', 'Impact', 'Close'].map((label, idx) => (
                  <span key={idx} className="text-xs text-[#64748B]">{label}</span>
                ))}
              </div>
            </div>

            <Button
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="bg-[#95BF47] hover:bg-[#7fa93d] text-white disabled:opacity-30"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          {/* Keyboard hint */}
          <p className="text-center text-[#64748B] text-xs mt-4">
            Use ← → arrow keys or spacebar to navigate
          </p>

          {/* Slide Thumbnails */}
          <div className="mt-6 overflow-x-auto pb-4">
            <div className="flex gap-2 min-w-max">
              {slides.map((slide, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-16 h-10 rounded-lg border-2 transition-all flex flex-col items-center justify-center text-xs ${
                    currentSlide === idx 
                      ? 'border-[#95BF47] bg-[#95BF47]/20' 
                      : 'border-[#95BF47]/20 bg-[#111827] hover:border-[#95BF47]/50'
                  }`}
                >
                  <span className="text-[#94A3B8] font-medium">{idx + 1}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-6 no-print">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[#95BF47]/20 to-[#111827] border border-[#95BF47]/40 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">Ready to Stop Guessing?</h2>
            <p className="text-[#94A3B8] mb-6">
              See where your pricing risk hides—before it costs you revenue.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/pricing-check">
                <Button className="bg-[#95BF47] hover:bg-[#7fa93d] text-white px-8 py-6">
                  Run Free Pricing Check
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/ted-talk">
                <Button variant="outline" className="border-[#95BF47]/50 text-[#F8FAFC] hover:bg-[#95BF47]/10 px-8 py-6">
                  View Original TED Talk
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShopifyTEDTalkPage;
